import { supabase } from '../supabase';
import { cloneProgram, discardUnassignedCopy } from './programCloneService';

// "Build: Upper/Lower — Beginner" -> "Build: Upper/Lower". The client sees the program, not its level.
const clientCopyName = (templateName) => templateName
  .replace(/\s*[—\-/]\s*(Beginner|Intermediate|Advanced)\s*(?=\)|$)/gi, '')
  .replace(/\s*[—\-/]\s*(Beginner|Intermediate|Advanced)\s*/gi, ' ')
  .replace(/\s+/g, ' ')
  .trim();

// supabase-js resolves a failed request as { error } rather than throwing.
const check = (label, { data, error }) => {
  if (error) throw new Error(`${label}: ${error.message || 'request failed'}`);
  return data;
};

// Switches the given assignments back on. Returns true only if at least one row really was
// reactivated, which also proves no other assignment is active for this client: the
// one-active-assignment-per-user index (idx_program_assignments_active_user, migration 002) would
// have refused the update otherwise.
const restoreAssignments = async (ids) => {
  if (ids.length === 0) return false;
  const { data, error } = await supabase
    .from('program_assignments')
    .update({ is_active: true, ended_at: null })
    .in('id', ids)
    .select('id');
  return !error && (data?.length ?? 0) > 0;
};

/**
 * Assigns a program to a client: clones it into a private copy and makes that copy the client's
 * one active assignment. Returns the copy's id.
 *
 * ORDER MATTERS. The clone is a dozen requests and by far the likeliest step to fail, so it runs
 * first, while the client's current program is still active: a failed clone changes nothing. This
 * used to end the current assignment FIRST (unchecked) and then clone for several seconds, so one
 * dropped request left the client with no program at all.
 *
 * Ending the old assignment and inserting the new one are still two requests, not a transaction.
 * If either fails, the old assignment is switched back on. That is safe even when the insert
 * actually committed and only its response was lost: the unique index then refuses the restore
 * and the new program stays. The copy is put aside (archived, and removed as far as the access
 * rules allow) only when the restore proves it is unassigned.
 *
 * @param {object} options
 * @param {string} options.userId    - The client's auth UUID
 * @param {string|number} options.programId - The program to copy for them
 * @returns {Promise<string|number>} The client copy's program id
 */
export const assignProgramToClient = async ({ userId, programId }) => {
  const template = check('Could not read the program', await supabase
    .from('programs')
    .select('name')
    .eq('id', programId)
    .single());

  const clientCopyId = await cloneProgram({
    programId,
    newName: clientCopyName(template.name),
    userId,
    isClientCopy: true,
    sourceProgramId: programId,
  });

  // Read which assignment is active BEFORE touching it, so it can be restored by id even if the
  // response to the update below is lost.
  const { data: current, error: currentErr } = await supabase
    .from('program_assignments')
    .select('id')
    .eq('user_id', userId)
    .eq('is_active', true);
  if (currentErr) {
    await discardUnassignedCopy(clientCopyId);
    throw new Error(`Could not check the client's current program: ${currentErr.message}. Nothing was changed.`);
  }
  const previousIds = (current || []).map((a) => a.id);

  try {
    if (previousIds.length > 0) {
      check('Could not end the current program', await supabase
        .from('program_assignments')
        .update({ is_active: false, ended_at: new Date().toISOString() })
        .in('id', previousIds));
    }

    check('Could not assign the program', await supabase
      .from('program_assignments')
      .insert({
        program_id: clientCopyId,
        user_id: userId,
        is_active: true,
        assigned_at: new Date().toISOString(),
        current_week: 1,
      }));
  } catch (err) {
    if (await restoreAssignments(previousIds)) {
      await discardUnassignedCopy(clientCopyId);
      throw new Error(`${err.message}. The client's previous program is still active.`);
    }
    // Nothing was restored: either there was no previous program, or the restore was refused
    // because something else is active (possibly the new program itself, if only the insert's
    // response was lost). The copy may be assigned, so it is left alone.
    throw new Error(`${err.message}. Check which program the client has now before trying again.`);
  }

  return clientCopyId;
};
